import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import ClientPortalLayout from "../ClientPortalLayout";
import DocumentUploadPanel from "./DocumentUploadPanel";
import HouseholdSummary from "./HouseholdSummary";
import clientAxiosInstance from "../../api/clientAxiosInstance";
import { useConfirm } from "../../context/ConfirmContext";


const flattenSuggestedDocuments = (suggestedDocuments, householdData) => {
  if (!suggestedDocuments) return [];

  const rows = [];
  const members = householdData?.household_members || householdData?.members || [];
  const accounts = householdData?.accounts || [];

  // Helper to resolve member name
  const getMemberName = (memberId) => {
    const member = members.find((m) => m.id === memberId);
    if (!member) return null;
    let personalInfo = member.personal_info || {};
    if (typeof personalInfo === "string") {
      try { personalInfo = JSON.parse(personalInfo); } catch { personalInfo = {}; }
    }
    const name = `${personalInfo.first_name || ""} ${personalInfo.last_name || ""}`.trim();
    const isPrimary = member.is_primary === true;
    return { name: name || "Unknown Member", isPrimary };
  };

  const getAccountDisplayName = (accountId) => {
    const account = accounts.find((a) => a.id === accountId);
    if (!account) return "Account";

    const registration = account.registration || account.type || "Account";
    const ownerNames = (account.owners_info || [])
      .map((owner) => {
        const member = members.find((m) => m.id === owner.id);
        if (!member) return null;
        let pi = member.personal_info || {};
        if (typeof pi === "string") {
          try { pi = JSON.parse(pi); } catch { pi = {}; }
        }
        return pi.first_name || null;
      })
      .filter(Boolean);

    if (ownerNames.length > 0) {
      return `${registration} — ${ownerNames.join(", ")}`;
    }
    return registration;
  };

  // 1. Member documents (e.g. ID Document per member)
  const memberDocs = suggestedDocuments.member_documents || {};
  Object.entries(memberDocs).forEach(([docType, entries]) => {
    if (!Array.isArray(entries)) return;

    // Group entries for display — mandatory first, then optional
    const mandatoryEntries = entries.filter((e) => e.mandatory);
    const optionalEntries = entries.filter((e) => !e.mandatory);

    // Show each member individually — mandatory first, then optional
    mandatoryEntries.forEach((entry) => {
      const memberInfo = getMemberName(entry.member_id);
      rows.push({
        key: `member-${docType}-${entry.member_id}`,
        docType: docType + " / Driver's License",
        relatedTo: memberInfo
          ? `${memberInfo.name}${memberInfo.isPrimary ? " (Primary)" : ""}`
          : "Household Member",
        mandatory: true,
        status: entry.status,
        memberId: entry.member_id,
        category: "member",
      });
    });

    optionalEntries.forEach((entry) => {
      const memberInfo = getMemberName(entry.member_id);
      rows.push({
        key: `member-${docType}-${entry.member_id}`,
        docType: docType + " / Driver's License",
        relatedTo: memberInfo
          ? `${memberInfo.name}`
          : "Household Member",
        mandatory: false,
        status: entry.status,
        memberId: entry.member_id,
        category: "member",
      });
    });
  });

  // 2. Static row for additional unlisted members' ID documents
  rows.push({
    key: "static-id-additional-members",
    docType: "ID Document / Driver's License",
    relatedTo: "Additional unlisted household members — account owners, beneficiaries, trusted contacts",
    mandatory: false,
    status: null,
    category: "member",
  });

  // 3. Account documents — Brokerage Statements first, then Trust Certificates, then others
  const accountDocs = suggestedDocuments.account_documents || {};
  const accountDocOrder = ["Brokerage Statement", "Trust Certificate"];

  // Add ordered doc types first
  accountDocOrder.forEach((docType) => {
    const entries = accountDocs[docType];
    if (!Array.isArray(entries)) return;
    entries.forEach((entry) => {
      const accountDisplay = getAccountDisplayName(entry.account_id);
      rows.push({
        key: `account-${docType}-${entry.account_id}`,
        docType,
        relatedTo: accountDisplay,
        mandatory: true,
        status: entry.status,
        accountId: entry.account_id,
        category: "account",
      });
    });
  });

  // Add any remaining account doc types not in the ordered list
  Object.entries(accountDocs).forEach(([docType, entries]) => {
    if (accountDocOrder.includes(docType)) return;
    if (!Array.isArray(entries)) return;
    entries.forEach((entry) => {
      const accountDisplay = getAccountDisplayName(entry.account_id);
      rows.push({
        key: `account-${docType}-${entry.account_id}`,
        docType,
        relatedTo: accountDisplay,
        mandatory: true,
        status: entry.status,
        accountId: entry.account_id,
        category: "account",
      });
    });
  });

  // 4. General documents — supports both old (string[]) and new (object) format
  const generalDocs = suggestedDocuments.general_documents;
  const bankAccounts = householdData?.bank_accounts || [];

  const getBankAccountName = (bankAccountId) => {
    const ba = bankAccounts.find((b) => b.id === bankAccountId);
    if (!ba) return "Bank Account";
    const name = ba.bank_name || "Bank";
    const num = ba.account_number || "";
    return num ? `${name} — ${num}` : name;
  };

  if (generalDocs && typeof generalDocs === "object" && !Array.isArray(generalDocs)) {
    // New format: { "Bank Statements": [{ id, status }, ...] }
    Object.entries(generalDocs).forEach(([docType, entries]) => {
      if (!Array.isArray(entries)) return;
      entries.forEach((entry) => {
        const bankName = getBankAccountName(entry.id);
        rows.push({
          key: `general-${docType}-${entry.id}`,
          docType: "Bank Statement / Voided Check",
          relatedTo: bankName,
          mandatory: false,
          status: entry.status,
          bankAccountId: entry.id,
          category: "general",
        });
      });
    });
  } else if (Array.isArray(generalDocs)) {
    // Legacy format: ["Bank Statement"]
    generalDocs.forEach((docType) => {
      rows.push({
        key: `general-${docType}`,
        docType: docType === "Bank Statement" ? "Bank Statement / Voided Check" : docType,
        relatedTo: "Any bank account to be linked to your brokerage accounts for deposits and withdrawals",
        mandatory: false,
        status: null,
        category: "general",
      });
    });
  }

  // 5. Static Bank Statement row — always present for additional uploads
  rows.push({
    key: "static-bank-statement",
    docType: "Bank Statement / Voided Check",
    relatedTo: "Additional bank accounts to be linked to your brokerage accounts for deposits and withdrawals",
    mandatory: false,
    status: null,
    category: "general",
  });

  return rows;
};

// Map SSE event status values to display status
const resolveStatus = (apiStatus) => {
  if (!apiStatus) return "missing";
  switch (apiStatus) {
    case "initiated":
      return "uploading";
    case "confirmed":
      return "queued";
    case "processing":
      return "processing";
    case "completed":
      return "uploaded";
    default:
      return "missing";
  }
};

const StatusBadge = ({ status }) => {
  switch (status) {
    case "uploading":
    case "queued":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200/60">
          <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          Queued
        </span>
      );
    case "processing":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200/60">
          <div className="animate-spin rounded-full h-3 w-3 border-[1.5px] border-blue-600 border-t-transparent" />
          Processing
        </span>
      );
    case "uploaded":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200/60">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          Uploaded
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200/60">
          Missing
        </span>
      );
  }
};

const DocIcon = () => (
  <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  </div>
);

const SAFETY_VALVE_MS = 15 * 60 * 1000; // 15 minutes

const DocumentUploadV2 = ({
  householdData,
  household_id,
  clientToken,
  onContinue,
  onDataUpdate,
  docsStatusSSE,
}) => {
  const confirmDialog = useConfirm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [clearingAdvisorNote, setClearingAdvisorNote] = useState(false);
  const [safetyValveExpired, setSafetyValveExpired] = useState(false);
  const [uploadConfirmedAt, setUploadConfirmedAt] = useState(null);
  const safetyTimerRef = useRef(null);

  const demoVideoUrl = process.env.REACT_APP_DEMO_VIDEO_URL || "";

  // Called by DocumentUploadPanel after confirm-upload API succeeds
  const handleUploadConfirmed = useCallback(() => {
    setUploadConfirmedAt(new Date().toISOString());
    setSafetyValveExpired(false);
  }, []);

  // Resolve docs_status: SSE payload takes precedence over API data
  const docsStatus = docsStatusSSE || householdData?.docs_status || null;

  // Determine effective last_upload_at: use the most recent between
  // docsStatus.last_upload_at and the local uploadConfirmedAt
  const effectiveLastUploadAt = useMemo(() => {
    const apiTime = docsStatus?.last_upload_at ? new Date(docsStatus.last_upload_at).getTime() : 0;
    const localTime = uploadConfirmedAt ? new Date(uploadConfirmedAt).getTime() : 0;
    if (localTime > apiTime) return uploadConfirmedAt;
    return docsStatus?.last_upload_at || null;
  }, [docsStatus?.last_upload_at, uploadConfirmedAt]);

  // canProceed logic per spec
  const canProceed = useMemo(() => {
    // If user just confirmed an upload and we haven't received SSE/API update yet, disable
    if (uploadConfirmedAt && !docsStatusSSE) {
      const apiTime = docsStatus?.last_upload_at ? new Date(docsStatus.last_upload_at).getTime() : 0;
      const localTime = new Date(uploadConfirmedAt).getTime();
      // Local confirm is newer than what API knows — docs are in flight
      if (localTime > apiTime) {
        return safetyValveExpired;
      }
    }

    if (!docsStatus || docsStatus.total === 0) return true;

    const pendingCount =
      (docsStatus.processing || 0) +
      (docsStatus.initiated || 0) +
      (docsStatus.confirmed || 0);

    if (pendingCount === 0) return true;
    if (safetyValveExpired) return true;

    return false;
  }, [docsStatus, docsStatusSSE, safetyValveExpired, uploadConfirmedAt]);

  // 15-minute safety valve timer
  useEffect(() => {
    // Clear any existing timer
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }

    // Determine the timestamp to base the timer on
    const lastUpload = effectiveLastUploadAt;

    if (!lastUpload) {
      setSafetyValveExpired(false);
      return;
    }

    // Check if there's actually something pending
    const hasPending = uploadConfirmedAt || (docsStatus && (
      (docsStatus.processing || 0) +
      (docsStatus.initiated || 0) +
      (docsStatus.confirmed || 0) > 0
    ));

    if (!hasPending) {
      setSafetyValveExpired(false);
      return;
    }

    const uploadTime = new Date(lastUpload).getTime();
    const expiresAt = uploadTime + SAFETY_VALVE_MS;
    const remainingMs = expiresAt - Date.now();

    if (remainingMs <= 0) {
      setSafetyValveExpired(true);
    } else {
      setSafetyValveExpired(false);
      safetyTimerRef.current = setTimeout(() => {
        setSafetyValveExpired(true);
      }, remainingMs);
    }

    return () => {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    };
  }, [effectiveLastUploadAt, docsStatus, uploadConfirmedAt]);

  // Clear local uploadConfirmedAt once SSE/API catches up
  useEffect(() => {
    if (!uploadConfirmedAt || !docsStatus?.last_upload_at) return;
    const apiTime = new Date(docsStatus.last_upload_at).getTime();
    const localTime = new Date(uploadConfirmedAt).getTime();
    if (apiTime >= localTime) {
      setUploadConfirmedAt(null);
    }
  }, [docsStatus?.last_upload_at, uploadConfirmedAt]);

  const handleClearAdvisorNote = useCallback(async () => {
    if (!household_id || !clientToken || clearingAdvisorNote) return;
    setClearingAdvisorNote(true);
    try {
      await clientAxiosInstance.delete(
        `/client/household/${household_id}/advisor-note`,
        {
          headers: { Authorization: `Bearer ${clientToken}` },
        },
      );
      if (onDataUpdate) onDataUpdate();
    } catch (e) {
      console.error("Failed to clear advisor note:", e);
    } finally {
      setClearingAdvisorNote(false);
    }
  }, [household_id, clientToken, clearingAdvisorNote, onDataUpdate]);

  const confirmClearAdvisorNote = useCallback(async () => {
    if (!householdData?.advisor_note || householdData.advisor_note.trim() === "")
      return;
    const ok = await confirmDialog({
      title: "Delete advisor note?",
      message: "This note will be removed and cannot be shown again.",
      confirmLabel: "Delete",
    });
    if (ok) handleClearAdvisorNote();
  }, [householdData?.advisor_note, handleClearAdvisorNote, confirmDialog]);

  // Flatten suggested_documents into displayable rows
  const documentRows = useMemo(
    () => flattenSuggestedDocuments(householdData?.suggested_documents, householdData),
    [householdData]
  );

  // Resolve each row's display status
  const documentsWithStatus = useMemo(
    () =>
      documentRows.map((row) => ({
        ...row,
        displayStatus: resolveStatus(row.status),
      })),
    [documentRows]
  );

  // Counts
  const uploadedCount = documentsWithStatus.filter(
    (d) => d.displayStatus === "uploaded" || d.displayStatus === "processing" || d.displayStatus === "queued"
  ).length;

  const handleSaveAndContinue = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      await clientAxiosInstance.put(
        `/client/household/${household_id}`,
        { action: "onboarding_done" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${clientToken}`,
          },
        }
      );

      if (onContinue) onContinue();
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Failed to update. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Header action button
  const headerAction = (
    <button
      onClick={handleSaveAndContinue}
      disabled={!canProceed || isSubmitting}
      className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold text-sm transition duration-200 flex items-center gap-2 shadow-sm"
    >
      {isSubmitting ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          Saving...
        </>
      ) : (
        <>
          Save & Continue
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </>
      )}
    </button>
  );

  return (
    <ClientPortalLayout
      householdData={householdData}
      currentStep="document-upload"
      title="Upload your documents"
      subtitle="We'll use them to auto-populate data. Recommended docs are listed below."
      headerAction={headerAction}
      demoVideoUrl={demoVideoUrl}
    >
      {householdData?.advisor_note &&
        householdData.advisor_note.trim() !== "" && (
          <div className="mb-4 flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h8M8 14h6m-2 7H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v8l-4 4z"
                />
              </svg>
              <div>
                <p className="text-xs font-semibold text-emerald-800">
                  Message from your advisor
                </p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {householdData.advisor_note}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={confirmClearAdvisorNote}
              disabled={clearingAdvisorNote}
              className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {clearingAdvisorNote ? "Deleting..." : "Delete"}
            </button>
          </div>
        )}

      {/* Two-column layout: summary + table (left) | upload panel (right) */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left — Summary + Requested Documents */}
        <div className="flex-1 min-w-0">
          {/* Household Summary */}
          <HouseholdSummary householdData={householdData} />

          <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-[1.0625rem] font-bold text-gray-900">Recommended Documents</h2>
              {uploadedCount > 0 && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                  {uploadedCount} Uploaded
                </span>
              )}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-5 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      Document Type
                    </th>
                    <th className="px-5 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      Related To
                    </th>
                    <th className="px-5 py-3 text-left text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {documentsWithStatus.length > 0 ? (
                    documentsWithStatus.map((doc) => (
                      <tr key={doc.key} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <DocIcon />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {doc.docType}
                              </span>
                              {!doc.mandatory && (
                                <span className="ml-2 text-[10px] font-medium text-gray-400 italic">
                                  Optional
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div>
                            <p className="text-sm text-gray-600 leading-snug">
                              {doc.relatedTo || "—"}
                            </p>
                            {doc.relatedToSub && (
                              <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                {doc.relatedToSub}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center">
                            <StatusBadge status={doc.displayStatus} />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-5 py-10 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm text-gray-500">No requested documents found.</p>
                          <p className="text-xs text-gray-400">Upload documents using the panel on the right.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden divide-y divide-gray-100">
              {documentsWithStatus.length > 0 ? (
                documentsWithStatus.map((doc) => (
                  <div key={doc.key} className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <DocIcon />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {doc.docType}
                          {!doc.mandatory && (
                            <span className="ml-1.5 text-[10px] font-medium text-gray-400 italic">
                              Optional
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          {doc.relatedTo || "—"}
                        </p>
                        {doc.relatedToSub && (
                          <p className="text-[11px] text-emerald-600 mt-0.5 flex items-center gap-1">
                            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            {doc.relatedToSub}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center pl-11">
                      <StatusBadge status={doc.displayStatus} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <p className="text-sm text-gray-500">No requested documents found.</p>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Footer Note */}
          <p className="mt-4 text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            All documents are encrypted in transit and at rest.
          </p>
        </div>

        {/* Right — Upload Panel */}
        <div className="w-full lg:w-80 flex-shrink-0">
          <div className="lg:sticky lg:top-24">
            <DocumentUploadPanel
              household_id={household_id}
              clientToken={clientToken}
              disabled={false}
              onUploadConfirmed={handleUploadConfirmed}
            />
          </div>
        </div>
      </div>
    </ClientPortalLayout>
  );
};

export default DocumentUploadV2;
